import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './search.css';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const MARGIN = 8;

// A small floating menu anchored to a button (a labelled dialog; choices
// inside are aria-pressed buttons in labelled groups). It renders into document.body
// with position: fixed, so it is never clipped by a scrolling panel or by the
// react-select control it may sit in. Closes on outside mousedown and Escape
// (focus goes back to the anchor). ArrowUp / ArrowDown move between items.
// React events from inside still bubble to React ancestors, so the root
// stops mousedown / keydown / touchend from reaching react-select.
export default function Popover({ anchorRef, open, onClose, children, width = 264, label, className }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const place = () => {
      const a = anchorRef && anchorRef.current;
      const p = ref.current;
      if (!a || !p) return;
      const r = a.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = Math.min(width, vw - MARGIN * 2);
      const h = p.offsetHeight;
      const left = Math.min(Math.max(MARGIN, r.left), vw - w - MARGIN);
      let top = r.bottom + 6;
      if (top + h > vh - MARGIN) {
        const above = r.top - 6 - h;
        top = above >= MARGIN ? above : Math.max(MARGIN, vh - MARGIN - h);
      }
      setPos((old) => (old && old.left === left && old.top === top && old.width === w ? old : { left, top, width: w }));
    };
    place();
    const raf = window.requestAnimationFrame ? window.requestAnimationFrame(place) : null;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      if (raf && window.cancelAnimationFrame) window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, width]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorRef && anchorRef.current && anchorRef.current.contains(e.target)) return;
      closeRef.current && closeRef.current();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
    };
  }, [open, anchorRef]);

  // focus the checked item (or the first one) when the menu opens
  useEffect(() => {
    if (!open || !pos || !ref.current) return;
    if (ref.current.contains(document.activeElement)) return;
    const el =
      ref.current.querySelector('[aria-pressed="true"]:not([disabled]), [aria-checked="true"]:not([disabled])') ||
      ref.current.querySelector(FOCUSABLE);
    if (el && el.focus) el.focus({ preventScroll: true });
  }, [open, pos]);

  if (!open) return null;

  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      closeRef.current && closeRef.current();
      const a = anchorRef && anchorRef.current;
      if (a && a.focus) a.focus();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const items = Array.prototype.slice.call(ref.current.querySelectorAll(FOCUSABLE));
      if (!items.length) return;
      e.preventDefault();
      const at = items.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at <= 0 ? items.length : at) - 1;
      items[next].focus();
    }
  };

  const stop = (e) => e.stopPropagation();
  const style = pos ? { left: pos.left, top: pos.top, width: pos.width } : { left: 0, top: 0, width, visibility: 'hidden' };

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className={'ss-pop' + (className ? ' ' + className : '')}
      style={style}
      role="dialog"
      aria-label={label}
      onMouseDown={stop}
      onTouchEnd={stop}
      onClick={stop}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>,
    document.body
  );
}
